import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Shield, Globe, Clock, Monitor, Smartphone, User } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface LiveSession {
  id: string;
  session_token: string;
  user_id?: string;
  device_fingerprint: any;
  browser_info: any;
  network_info: any;
  behavioral_data: any;
  location_data: any;
  created_at: string;
  last_activity: string;
  start_time: string;
  is_active: boolean;
  visitor_id: string;
  ip_address?: string;
  user_agent?: string;
  referrer?: string;
}

interface LiveSessionsViewProps {
  onBack: () => void;
}

export const LiveSessionsView = ({ onBack }: LiveSessionsViewProps) => {
  const [sessions, setSessions] = useState<LiveSession[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    fetchLiveSessions();
    
    // Set up real-time updates
    const interval = setInterval(fetchLiveSessions, 10000); // Update every 10 seconds
    
    return () => clearInterval(interval);
  }, []);

  const fetchLiveSessions = async () => {
    try {
      const { data, error } = await supabase
        .from('website_sessions')
        .select('*')
        .eq('is_active', true) // Only get active sessions
        .order('last_activity', { ascending: false })
        .limit(50);

      if (error) throw error;
      setSessions((data || []) as LiveSession[]);
    } catch (error) {
      console.error('Error fetching live sessions:', error);
      toast({
        title: "Error",
        description: "Failed to load live sessions",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const terminateSession = async (sessionId: string) => {
    try {
      await supabase
        .from('website_sessions')
        .update({
          is_active: false,
          last_activity: new Date().toISOString()
        })
        .eq('id', sessionId);

      toast({
        title: "Success",
        description: "Session terminated successfully",
      });
      
      await fetchLiveSessions();
    } catch (error) {
      console.error('Error terminating session:', error);
      toast({
        title: "Error",
        description: "Failed to terminate session",
        variant: "destructive"
      });
    }
  };

  const formatDuration = (startTime: string) => {
    const start = new Date(startTime);
    const now = new Date();
    const diff = now.getTime() - start.getTime();
    const minutes = Math.floor(diff / 60000);
    
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  };

  const getDeviceIcon = (deviceInfo: any) => {
    if (deviceInfo?.touchSupport) return <Smartphone className="w-4 h-4" />;
    return <Monitor className="w-4 h-4" />;
  };

  const getConnectionType = (networkInfo: any) => {
    return networkInfo?.effectiveType || 'unknown';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Live User Sessions</h2>
          <p className="text-gray-600">Real-time view of active user sessions</p>
        </div>
        <Button variant="outline" onClick={onBack}>
          Back to Login Attempts
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Sessions</CardTitle>
            <Globe className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{sessions.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Authenticated Users</CardTitle>
            <User className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{sessions.filter(s => s.user_id).length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Anonymous Sessions</CardTitle>
            <Shield className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{sessions.filter(s => !s.user_id).length}</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Active Sessions</CardTitle>
        </CardHeader>
        <CardContent>
          {sessions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No active sessions found.
            </div>
          ) : (
            <div className="space-y-4">
              {sessions.map((session) => (
                <div
                  key={session.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
                >
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                      <Badge variant="outline">Live</Badge>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      {getDeviceIcon(session.device_fingerprint)}
                      <div>
                        <div className="font-medium">
                          {session.user_id ? `User ${session.user_id.slice(-8)}` : `Visitor ${session.visitor_id.slice(-8)}`}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {session.session_token?.slice(-12) || 'Unknown Token'}
                        </div>
                      </div>
                    </div>

                    <div className="text-sm text-muted-foreground">
                      <div className="flex items-center space-x-2">
                        <Clock className="w-3 h-3" />
                        <span>{formatDuration(session.start_time)}</span>
                      </div>
                      <div className="text-xs">
                        Connection: {getConnectionType(session.network_info)}
                      </div>
                    </div>

                    {session.location_data && typeof session.location_data === 'object' && (
                      <div className="text-sm text-muted-foreground">
                        <div>Location: {(session.location_data as any)?.timezone || (session.location_data as any)?.city || 'Unknown'}</div>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center space-x-2">
                    <div className="text-sm text-muted-foreground">
                      Last activity: {new Date(session.last_activity).toLocaleTimeString()}
                    </div>
                    <Button 
                      variant="destructive" 
                      size="sm"
                      onClick={() => terminateSession(session.id)}
                    >
                      Terminate
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};